import { Nullable } from '@tcc/common';
import { localizeUnits } from '../../../utils/processUnits';
import {
  LocaleGroup,
  LocalizedMeasurementQU,
  ReportMeasurement,
  YieldRegionReport
} from '../../../types';
import {
  COTTON_CROP_CODE,
  SILAGE_CORN_CROP_CODE,
  SUGARCANE_CROP_CODE
} from '../../../constants/cropCodes';
import { getDateFormatted, isValidDate } from '../../../utils/dateHelpers';
import { createUnit } from '../../../i18n/unitQtyWrapper';
import { capitalizeWords } from '../../../utils/strings';

type CropNominalWeight = Nullable<number | ReportMeasurement> | undefined;
const getCropNominalWeightValue = (cropNominalWeight?: CropNominalWeight): number | undefined => {
  if (typeof cropNominalWeight === 'number') {
    return cropNominalWeight;
  }

  if (cropNominalWeight && typeof cropNominalWeight === 'object' && 'v' in cropNominalWeight) {
    return cropNominalWeight.v;
  }

  return undefined;
};

export const getLocalizedArea = (area: ReportMeasurement): LocalizedMeasurementQU => localizeUnits(
  area.v,
  { key: area.u, localeGroup: LocaleGroup.AREA },
);

export const getLocalizedAreaLabel = (areaUnit: string): string => {
  const result = createUnit(2, areaUnit);
  const label = result.forLocaleGroup(
    LocaleGroup.AREA
  ).label.quantitySpecific;

  return capitalizeWords(label)
}

export const getLocalizedMoisture = (cropCode: string, averageMoisture: ReportMeasurement): LocalizedMeasurementQU | undefined => {
  const displayMoisture = ![SUGARCANE_CROP_CODE, COTTON_CROP_CODE].includes(cropCode);

  if (displayMoisture) {
    return localizeUnits(
      averageMoisture.v,
      { key: averageMoisture.u, localeGroup: LocaleGroup.MOISTURE }
    );
  }
  
  return undefined;
}

export const getLocalizedAverageYield = (
  cropCode: string,
  averageYield: ReportMeasurement,
  cropNominalWeight: CropNominalWeight
): LocalizedMeasurementQU => {
  switch (cropCode) {
    case SILAGE_CORN_CROP_CODE: {
      return localizeUnits(
        averageYield.v,
        {
          key: averageYield.u,
          localeGroup: LocaleGroup.YIELD_SILAGE,
          cropNominalWeight: getCropNominalWeightValue(cropNominalWeight)
        },
        { cropCode }
      );
    }

    case COTTON_CROP_CODE: {
      return localizeUnits(
        averageYield.v,
        {
          key: averageYield.u,
          localeGroup: LocaleGroup.YIELD_COTTON,
          cropNominalWeight: getCropNominalWeightValue(cropNominalWeight)
        },
        { cropCode }
      );
    }

    case SUGARCANE_CROP_CODE: {
      return localizeUnits(
        averageYield.v,
        {
          key: averageYield.u,
          localeGroup: LocaleGroup.YIELD_SUGARCANE,
          cropNominalWeight: getCropNominalWeightValue(cropNominalWeight)
        },
        { cropCode }
      );
    }

    default: {
      const cropNominalWeightValue = getCropNominalWeightValue(cropNominalWeight);
      const localeGroup = cropNominalWeightValue ? LocaleGroup.YIELD : LocaleGroup.YIELD_UNWEIGHTED;

      return localizeUnits(
        averageYield.v,
        {
          key: averageYield.u,
          localeGroup,
          cropNominalWeight: cropNominalWeightValue
        },
        { cropCode }
      );
    }
  }
}

interface GetLocalizedTotalWeightParams {
  cropCode: string,
  totalWeight: ReportMeasurement,
  cropNominalWeight: CropNominalWeight
}

export const getLocalizedTotalWeight = ({
  cropCode,
  totalWeight,
  cropNominalWeight
}: GetLocalizedTotalWeightParams): LocalizedMeasurementQU | undefined => {
  switch (cropCode) {
    case SILAGE_CORN_CROP_CODE: {
      return localizeUnits(
        totalWeight.v,
        {
          key: totalWeight.u,
          localeGroup: LocaleGroup.WEIGHT_DRY_YIELD,
          cropNominalWeight: getCropNominalWeightValue(cropNominalWeight)
        },
        { cropCode }
      );
    }

    case COTTON_CROP_CODE: {
      return localizeUnits(
        totalWeight.v,
        {
          key: totalWeight.u,
          localeGroup: LocaleGroup.WEIGHT_COTTON,
          cropNominalWeight: getCropNominalWeightValue(cropNominalWeight)
        },
        { cropCode }
      );
    }

    case SUGARCANE_CROP_CODE: {
      return localizeUnits(
        totalWeight.v,
        {
          key: totalWeight.u,
          localeGroup: LocaleGroup.WEIGHT,
          cropNominalWeight: getCropNominalWeightValue(cropNominalWeight)
        },
        { cropCode }
      );
    }

    default: {
      const cropNominalWeightValue = getCropNominalWeightValue(cropNominalWeight);
      const localeGroup = cropNominalWeightValue ? LocaleGroup.WEIGHT_YIELD : LocaleGroup.WEIGHT_YIELD_UNWEIGHTED;
      
      return localizeUnits(
        totalWeight.v,
        {
          key: totalWeight.u,
          localeGroup,
          cropNominalWeight: cropNominalWeightValue
        },
        { cropCode }
      );
    }
  }
}

export const getDataWithFormattedDates = (data: YieldRegionReport[], locale: string): YieldRegionReport[] => data.map((item) => {
  if (isValidDate(item.label)) {
    const formattedDate = getDateFormatted(new Date(item.label), locale);
    return { ...item, label: formattedDate };
  }

  return item;
})